/**
 * 一键完成：一行下单收成一组完成块。
 * 1. 是 A 区里符合「类型-长度-后缀」的在库域名。
 * 2. 是 S 区任意一条在库域名。组数是要几组这样的搭配。
 */

export type FinishStyle = 'NUMBER' | 'ALPHA' | 'MIX' | 'REVIEW'

export type FinishOrder = {
  style: FinishStyle
  styleLabel: string
  charCount: number
  suffix: string
  manager: string
  count: number
}

const STYLE_BY_LABEL: Record<string, FinishStyle> = {
  数字: 'NUMBER',
  字母: 'ALPHA',
  混合: 'MIX',
  其他: 'REVIEW',
}

const SPEC_ONLY = /^(数字|字母|混合|其他)-\d+-[a-zA-Z]{2,24}$/

/** 条件可以单独一行，管理员和组数写在下一行。空行忽略。已经写在一行里的保持原样。 */
export function foldOrderLines(raw: string): string[] {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
  const out: string[] = []
  for (let i = 0; i < lines.length; i += 1) {
    if (SPEC_ONLY.test(lines[i]) && i + 1 < lines.length && !SPEC_ONLY.test(lines[i + 1])) {
      out.push(`${lines[i]} ${lines[i + 1]}`)
      i += 1
    } else {
      out.push(lines[i])
    }
  }
  return out
}

export function parseOrderLine(
  raw: string,
  managers: string[],
): { ok: true; order: FinishOrder } | { ok: false; error: string } {
  const line = raw.trim()
  if (!line) return { ok: false, error: '空行' }
  const parts = line.split(/\s+/)
  if (parts.length < 2) {
    return { ok: false, error: `「${line}」缺少管理员。格式：数字-5-uk  舒心棋牌1` }
  }
  const spec = parts[0]
  const rest = parts.slice(1).join('')
  const matched = spec.match(/^(数字|字母|混合|其他)-(\d+)-([a-zA-Z]{2,24})$/)
  if (!matched) {
    return { ok: false, error: `「${spec}」不是 类型-长度-后缀，例如 数字-5-uk` }
  }
  const charCount = Number(matched[2])
  if (charCount < 1 || charCount > 63) {
    return { ok: false, error: `「${spec}」的长度要在 1 到 63 之间` }
  }
  const known = [...managers].filter(Boolean).sort((a, b) => b.length - a.length)
  const manager = known.find((name) => rest === name || rest.startsWith(name))
  if (!manager) {
    return { ok: false, error: `「${rest}」对不上配置里的管理员` }
  }
  const qtyText = rest.slice(manager.length).trim()
  if (!/^[1-9]\d*$/.test(qtyText)) {
    return { ok: false, error: `「${manager}」后面要写组数，例如 ${manager}1` }
  }
  const count = Number(qtyText)
  if (count > 50) {
    return { ok: false, error: `「${manager}」一次最多 50 组` }
  }
  return {
    ok: true,
    order: {
      style: STYLE_BY_LABEL[matched[1]],
      styleLabel: matched[1],
      charCount,
      suffix: '.' + matched[3].toLowerCase(),
      manager,
      count,
    },
  }
}

export function composeBlock(manager: string, upper: string, lower: string): string {
  return manager + '\n"1.' + upper + '\n2.' + lower + '"'
}

export function pairZones<T>(zoneA: T[], zoneS: T[], groups: number): {
  pairs: [T, T][]
  shortA: number
  shortS: number
} {
  const n = Math.min(groups, zoneA.length, zoneS.length)
  const pairs: [T, T][] = []
  for (let i = 0; i < n; i += 1) pairs.push([zoneA[i], zoneS[i]])
  return {
    pairs,
    shortA: Math.max(0, groups - zoneA.length),
    shortS: Math.max(0, groups - zoneS.length),
  }
}
